/* Matriz com escala de calor, porte de `renderTable()` / `renderMatriz()`
 * (legacy:809, :1086).
 *
 * Continua sendo uma <table> de verdade, e nao um heatmap de biblioteca: assim
 * e ordenavel, navegavel por leitor de tela, copiavel e exportavel. Trocar por
 * um grafico seria regressao, nao modernizacao.
 *
 * A rampa usa `--heat` como triplete RGB para o alpha variar sem recalcular cor,
 * o que faz a escala acompanhar o tema sozinha.
 *
 * Tres coisas foram acrescentadas depois, e todas moram aqui porque as tres
 * tabelas do painel sao esta:
 *
 *   summaryRows  linhas de resumo ACIMA dos rotulos de coluna -- a media geral na
 *                visao geral, a somatoria mensal por fluxo na aba por usuario.
 *   groupBy      agrupa por tipo de fluxo e fecha cada grupo com um subtotal.
 *   onPickCell   clicar numa celula abre os lancamentos que a formam.
 */

import { useMemo, useState } from "react";
import { FLOWS, cell, flowColor, shortName } from "../lib/format";

export interface MatrixRow {
  key: string; label: string; flow?: string; values: number[]; total?: number;
}

export interface SummaryRow {
  key: string; label: string; values: number[]; total?: number;
  flow?: string; title?: string;
}

export interface HeatMatrixProps {
  columns: { key: string; label?: string; flow?: string }[];
  rows: MatrixRow[];
  /** `row` normaliza dentro da linha (evolução no tempo); `column` entre linhas. */
  normalize: "row" | "column";
  heat: boolean;
  columnMax?: number[];
  onPickRow?: (key: string) => void;
  onPickCell?: (rowKey: string, columnKey: string) => void;
  firstColumnLabel?: string;
  /** Linhas de resumo, renderizadas acima da linha dos rótulos de coluna. */
  summaryRows?: SummaryRow[];
  /** Agrupa as linhas por tipo de fluxo, com subtotal ao fim de cada grupo. */
  groupBy?: "flow";
  activeRow?: string | null;
  activeColumn?: string | null;
}

const sumColumns = (rows: MatrixRow[], count: number) =>
  Array.from({ length: count }, (_, i) =>
    rows.reduce((total, row) => total + (row.values[i] ?? 0), 0));

export function HeatMatrix({
  columns, rows, normalize, heat, columnMax, onPickRow, onPickCell,
  firstColumnLabel = "", summaryRows, groupBy, activeRow, activeColumn,
}: HeatMatrixProps) {
  const [sort, setSort] = useState<{ column: number | null; dir: 1 | -1 }>({
    column: null, dir: -1,
  });

  const columnMaxes = useMemo(() => (
    columnMax ?? columns.map((_, i) =>
      Math.max(...rows.map((r) => Math.abs(r.values[i] ?? 0)), 0))
  ), [columns, rows, columnMax]);

  // Por chave, e nao por posicao: com ordenacao e agrupamento a linha muda de
  // lugar, e um maximo indexado por posicao passaria a sombrear a linha errada.
  const rowMaxes = useMemo(() => new Map(
    rows.map((r) => [r.key, Math.max(...r.values.map(Math.abs), 0)])), [rows]);

  /** Ordena — dentro do grupo, quando há grupo — e fecha cada grupo com o subtotal. */
  const groups = useMemo(() => {
    const ordered = sort.column === null ? rows : [...rows].sort((a, b) =>
      sort.dir * ((a.values[sort.column!] ?? 0) - (b.values[sort.column!] ?? 0)));
    if (groupBy !== "flow") return [{ key: "", rows: ordered, subtotal: null }];

    const buckets = new Map<string, MatrixRow[]>();
    for (const row of ordered) {
      const key = row.flow ?? "—";
      const bucket = buckets.get(key);
      if (bucket) bucket.push(row); else buckets.set(key, [row]);
    }
    const keys = [...buckets.keys()].sort((a, b) => {
      const ia = FLOWS.indexOf(a), ib = FLOWS.indexOf(b);
      return (ia < 0 ? FLOWS.length : ia) - (ib < 0 ? FLOWS.length : ib);
    });
    return keys.map((key) => {
      const groupRows = buckets.get(key)!;
      return {
        key,
        rows: groupRows,
        subtotal: {
          values: sumColumns(groupRows, columns.length),
          total: groupRows.reduce(
            (t, r) => t + (r.total ?? r.values.reduce((a, b) => a + b, 0)), 0),
        },
      };
    });
  }, [rows, sort, groupBy, columns.length]);

  const shade = (value: number, rowKey: string, colIndex: number) => {
    if (!heat || !value) return undefined;
    const max = normalize === "column" ? columnMaxes[colIndex] : rowMaxes.get(rowKey);
    if (!max) return undefined;
    const alpha = Math.min(0.42, (Math.abs(value) / max) * 0.42);
    return { background: `rgba(var(--heat), ${alpha.toFixed(3)})` };
  };

  const renderCell = (row: MatrixRow, column: { key: string }, colIndex: number) => {
    const value = row.values[colIndex] ?? 0;
    const picked = activeRow === row.key && activeColumn === column.key;
    return (
      <td key={column.key} className={`num${picked ? " picked" : ""}`}
          style={shade(value, row.key, colIndex)}>
        {onPickCell ? (
          <button type="button" className="cellbtn"
                  title={`Ver os lançamentos de ${shortName(row.label)} em ${column.key}`}
                  onClick={() => onPickCell(row.key, column.key)}>
            {cell(value)}
          </button>
        ) : cell(value)}
      </td>
    );
  };

  return (
    <table className="matrix">
      <thead>
        {summaryRows?.map((summary) => (
          <tr className="summary" key={summary.key} title={summary.title}>
            <th scope="row">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {summary.flow && (
                  <span aria-hidden style={{ width: 7, height: 7, borderRadius: 2,
                                             background: flowColor(summary.flow) }} />
                )}
                {summary.label}
              </span>
            </th>
            {columns.map((column, index) => (
              <th key={column.key} className="num">{cell(summary.values[index] ?? 0)}</th>
            ))}
            <th className="num">
              {cell(summary.total ?? summary.values.reduce((a, b) => a + b, 0))}
            </th>
          </tr>
        ))}
        <tr>
          <th scope="col">{firstColumnLabel}</th>
          {columns.map((column, index) => (
            <th key={column.key} scope="col" className="num sortable"
                title={`Ordenar por ${column.label ?? column.key}`}
                onClick={() => setSort((s) => ({
                  column: index,
                  dir: s.column === index && s.dir === -1 ? 1 : -1,
                }))}>
              {column.label ?? shortName(column.key)}
              {sort.column === index ? (sort.dir === -1 ? " ↓" : " ↑") : ""}
            </th>
          ))}
          <th scope="col" className="num">total</th>
        </tr>
      </thead>
      {groups.map((group) => (
        <tbody key={group.key || "todos"}>
          {group.rows.map((row) => (
            <tr key={row.key} className={activeRow === row.key ? "picked" : undefined}>
              <th scope="row" style={{ fontWeight: 400, whiteSpace: "nowrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {row.flow && (
                    <span aria-hidden style={{ width: 7, height: 7, borderRadius: 2,
                                               background: flowColor(row.flow) }} />
                  )}
                  {onPickRow ? (
                    <button className="chip" style={{ border: 0, padding: 0, background: "none" }}
                            onClick={() => onPickRow(row.key)}>{row.label}</button>
                  ) : row.label}
                </span>
              </th>
              {columns.map((column, colIndex) => renderCell(row, column, colIndex))}
              <td className="num" style={{ fontWeight: 600 }}>
                {cell(row.total ?? row.values.reduce((a, b) => a + b, 0))}
              </td>
            </tr>
          ))}
          {group.subtotal && (
            <tr className="subtotal">
              <th scope="row">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span aria-hidden style={{ width: 7, height: 7, borderRadius: 2,
                                             background: flowColor(group.key) }} />
                  subtotal {group.key.toLowerCase()}
                </span>
              </th>
              {columns.map((column, index) => (
                <td key={column.key} className="num">
                  {cell(group.subtotal!.values[index] ?? 0)}
                </td>
              ))}
              <td className="num">{cell(group.subtotal.total)}</td>
            </tr>
          )}
        </tbody>
      ))}
    </table>
  );
}
